// ─── Repeats: a procedure step performed N times ─────────────────────────────
//
// Mirrors `cgt-backend/src/procedure-templates/repeats.ts`. The server decides
// how many repeats a step has and what state each is in; this module is only
// about reading that answer consistently on every screen that shows steps.
//
// The one rule worth stating: a step that does NOT repeat is a step with
// exactly one repeat. Every helper here returns the same shape for both, so no
// surface needs an "is this repeating?" branch to count, render or navigate —
// which is what keeps a 1x step rendering exactly as it did before repeats
// existed.

import { REPEAT_ACTION } from '@/lib/formActions'
import type { FormDefinition, ProcedureJourneyRepeat, ProcedureJourneyStep } from '@/types/cgt'

/** Does this form offer the structural `Repeat` action at all? */
export function formOffersRepeat(form: Pick<FormDefinition, 'allowedActions'>): boolean {
  return form.allowedActions?.includes(REPEAT_ACTION) ?? false
}

/**
 * The count a newly added template step should start at.
 *
 * Reads the action, not just the number: a form that had `Repeat` switched off
 * may still hold a stale default, and seeding from it would resurrect a count
 * nobody chose. Mirrors `seedRepeatCount` on the server, which is the authority
 * — this exists so the input is correct before the first save, not instead of
 * the server's rule.
 */
export function repeatSeedFor(
  form: Pick<FormDefinition, 'allowedActions' | 'defaultRepeatCount'>,
): number | null {
  if (!formOffersRepeat(form)) return null
  return form.defaultRepeatCount ?? null
}

/**
 * The independently-completable units of a step: its repeats when it has them,
 * otherwise the step itself as a single unit.
 *
 * This is what progress counts iterate. A 3x step is three units, so it
 * contributes 3 to a denominator — the ticket's "case progress counts repeats
 * individually".
 */
export function stepUnits(step: ProcedureJourneyStep): ProcedureJourneyRepeat[] {
  if (step.repeats && step.repeats.length > 0) return step.repeats
  return [{ repeatIndex: 1, status: step.status, submissionId: step.submissionId }]
}

/** Does this step run more than once? The only place "repeating" is decided. */
export function isRepeatingStep(step: ProcedureJourneyStep): boolean {
  return (step.repeatCount ?? 1) > 1
}

export interface JourneyProgress {
  completed: number
  total: number
  /** Whole percent, 0 when there is nothing to do. */
  pct: number
}

/**
 * "X of Y completed" across a journey, counting each repeat separately.
 *
 * Shared rather than inlined per surface because the sidebar, the run list and
 * the board all show a version of this number, and three copies of the count
 * are three chances for one of them to keep saying "1 of 2" while another says
 * "1 of 4" for the same case.
 */
export function journeyProgress(steps: ProcedureJourneyStep[]): JourneyProgress {
  let completed = 0
  let total = 0
  for (const step of steps) {
    for (const unit of stepUnits(step)) {
      total += 1
      // Skipped on purpose is done for the run, as the server counts it.
      if (unit.status === 'completed' || unit.status === 'skipped') completed += 1
    }
  }
  return { completed, total, pct: total ? Math.round((completed / total) * 100) : 0 }
}

/**
 * The submission a CROSS-FORM reference (`{handle.fieldId}`) reads from.
 *
 * For a repeating step this is always REPEAT 1, never the step's own
 * `submissionId`. The step's default submission is "the first repeat still
 * needing work", which is the right thing to open and exactly the wrong thing
 * to reference: it advances as repeats are completed, so a downstream formula
 * would silently change value — and re-render a signed form's inputs — as the
 * case progressed.
 *
 * Repeat 1 is stable for the life of the run. Full per-repeat referencing
 * (`{handle[2].fieldId}` or similar) is deliberately a separate ticket; this
 * just makes the current behaviour defined instead of incidental.
 */
export function crossFormSubmissionId(step: ProcedureJourneyStep): string | null {
  if (!step.repeats || step.repeats.length === 0) return step.submissionId
  return step.repeats.find(r => r.repeatIndex === 1)?.submissionId ?? null
}

/**
 * The three buckets a repeating step's performances fall into.
 *
 * `upcoming` is everything neither done nor started — the spec's third bucket,
 * and the reason the journey emits a slot per declared repeat rather than one
 * per existing submission: a repeat nobody has opened yet still has to be
 * countable and showable as "next in line".
 *
 * `locked` repeats (the step's predecessors are not met) count as upcoming.
 * From the user's side they are the same thing — not done, not started — and
 * splitting them would put a fourth bucket on screen that the spec does not ask
 * for and that says nothing the step's own lock state does not already say.
 */
export interface RepeatBuckets {
  completed: ProcedureJourneyRepeat[]
  inProgress: ProcedureJourneyRepeat[]
  upcoming: ProcedureJourneyRepeat[]
}

export function bucketRepeats(step: ProcedureJourneyStep): RepeatBuckets {
  const buckets: RepeatBuckets = { completed: [], inProgress: [], upcoming: [] }
  for (const repeat of stepUnits(step)) {
    if (repeat.status === 'completed') buckets.completed.push(repeat)
    else if (repeat.status === 'in_progress') buckets.inProgress.push(repeat)
    else buckets.upcoming.push(repeat)
  }
  return buckets
}

/**
 * The next performance to work on: the lowest-numbered repeat not yet finished.
 *
 * This is the spec's "Show next will expose the next FORM in line". Undefined
 * once every repeat is complete, which is what tells a caller there is nothing
 * left to expose rather than to re-open the last one.
 */
export function nextRepeat(step: ProcedureJourneyStep): ProcedureJourneyRepeat | undefined {
  return stepUnits(step).find(r => r.status !== 'completed')
}

/**
 * One step's own progress: "3 / 10", as the spec words it.
 *
 * Deliberately per STEP, unlike `journeyProgress`, which spans the whole run.
 * A repeating step is a little journey of its own and the mockup gives it its
 * own bar.
 */
export function stepProgress(step: ProcedureJourneyStep): JourneyProgress {
  const units = stepUnits(step)
  const completed = units.filter(r => r.status === 'completed').length
  return {
    completed,
    total: units.length,
    pct: units.length ? Math.round((completed / units.length) * 100) : 0,
  }
}

/**
 * How a single repeat is labelled to a user: "Collection 2 of 3".
 *
 * The FORM's name, not the word "Step". A rail of rows all reading "Step 2 of
 * 3" says nothing about what is being performed — the performance is of a named
 * form, and that name is what a clinician is looking for when they scan the
 * list. The number follows it as a counter, so identical rows stay tellable
 * apart.
 */
export function repeatLabel(formName: string, repeatIndex: number, total: number): string {
  return `${formName} ${repeatIndex} of ${total}`
}

// There used to be a `repeatLockReason` here, explaining a repeat waiting on
// an earlier one. Repeats are no longer sequenced (z8nrz7e1be): the only
// thing that can lock a repeat now is its own step (an unmet predecessor, a
// lag window), which is the step's explanation to give, not a per-repeat
// one — callers show their own status label for a locked repeat instead.
